/**
 * Event Organization/Location Selector Component
 * For associating events with organizations and their locations
 */

import React, { useState, useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { supabase } from "../../services/supabase";
import type { UUID } from "../../types/common.types";
import Button from "../common/Button";
import Select from "../common/Select";
import { preventEnterSubmit } from "../../utils/preventEnterSubmit";

export interface EventOrgLocationSelection {
  org_id: UUID;
  org_name: string;
  location_id: UUID | null;
  location_name?: string;
}

// R1+R2 post-ship fix (2026-05-17): widened from { id, name } to
// include all fields the org dropdown reads on rows returned from
// supabase. Audit caught that the stale narrow interface was masking
// the actual structural shape we relied on at runtime (is_no_reseller,
// facility_id, promo_code, affiliate_id). The wider interface here is
// intentionally a LOCAL projection — broader Organization type lives
// in types/organization.types.ts but carries dozens of fields the
// selector doesn't need.
interface Organization {
  id: UUID;
  name: string;
  is_no_reseller?: boolean;
  facility_id?: string | null;
  promo_code?: string | null;
  affiliate_id?: string | null;
}

interface Location {
  id: UUID;
  name: string;
  organization_id: UUID;
  city?: string | null;
  address_line1?: string | null;
  /** Promo code on this location's own active CTA (what an event at this branch will carry). */
  promo?: string | null;
}

/** The organization's own (main) address, and the promo code on its organization-level CTA. */
interface OrgMain {
  address_line1: string | null;
  address_city: string | null;
  address_state: string | null;
  address_postal_code: string | null;
  address_country: string | null;
  affiliate_id: string | null;
  promo: string | null;
}

const sameCity = (a?: string | null, b?: string | null) =>
  !!a && !!b && a.trim().toLowerCase() === b.trim().toLowerCase();

interface EventOrganizationLocationSelectorProps {
  selected: EventOrgLocationSelection[];
  onChange: (selections: EventOrgLocationSelection[]) => void;
}

const EventOrganizationLocationSelector: React.FC<
  EventOrganizationLocationSelectorProps
> = ({ selected, onChange }) => {
  const [locations, setLocations] = useState<Record<UUID, Location[]>>({});
  // 2026-10-01: each branch's promo code and the organization's main address, so a scheduler can see
  // which code an event will carry and can add the main address as a selectable location.
  const [orgMain, setOrgMain] = useState<Record<UUID, OrgMain>>({});
  const [addingMain, setAddingMain] = useState(false);
  const [addMainError, setAddMainError] = useState<string | null>(null);
  const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null);
  const [selectedLocationId, setSelectedLocationId] = useState<UUID>("");
  const [orgSearchTerm, setOrgSearchTerm] = useState("");
  const [showOrgDropdown, setShowOrgDropdown] = useState(false);
  const orgDropdownRef = useRef<HTMLDivElement>(null);

  // Server-side org search. R2A (2026-05-17) adds:
  //   - facility_id / promo_code / affiliate_id in the projection so the
  //     dropdown can disambiguate organizations with similar names per
  //     Heather page 5 (e.g. "Elite Firearms" vs "Elite Firearms Training"
  //     vs "Elite Training Academy LLC").
  //   - is_no_reseller flag so the sentinel row is fetched + pinned.
  //   - search by identifier as well as name. Identifier-prefix matches
  //     rank above name matches; both are merged into a single result set.
  //
  // No Reseller is fetched separately and prepended to the result list
  // so it's always visible regardless of search term.
  const { data: orgResults = [] } = useQuery({
    queryKey: ["event-org-selector-autocomplete", orgSearchTerm],
    queryFn: async () => {
      const term = orgSearchTerm.trim();

      // Always include the No Reseller sentinel as a pinned first option.
      const { data: noResellerRow } = await supabase
        .from("organizations")
        .select(
          "id, name, facility_id, promo_code, affiliate_id, is_no_reseller",
        )
        .eq("is_no_reseller", true)
        .maybeSingle();

      // Identifier-prefix matches first (higher signal).
      //
      // R1+R2 post-ship fix (2026-05-17): sanitize the search term
      // before interpolating into the PostgREST .or() filter string.
      // Comma, paren, period, asterisk, and double-quote are PostgREST
      // grammar characters and would break the query syntax. Stripping
      // them is safer + simpler than escaping for a search box where
      // operators type identifier prefixes (which are alphanumeric +
      // dashes anyway). Audit must-fix.
      let identifierMatches: Organization[] = [];
      const sanitizedTerm = term ? term.replace(/[,()."*\\]/g, "").trim() : "";
      if (sanitizedTerm) {
        const { data: idMatches } = await supabase
          .from("organizations")
          .select(
            "id, name, facility_id, promo_code, affiliate_id, is_no_reseller",
          )
          .or(
            `facility_id.ilike.${sanitizedTerm}%,promo_code.ilike.${sanitizedTerm}%,affiliate_id.ilike.${sanitizedTerm}%`,
          )
          .eq("is_archived", false)
          .eq("is_no_reseller", false)
          .order("name", { ascending: true })
          .limit(10);
        identifierMatches = (idMatches ?? []) as Organization[];
      }

      // Name matches (always queried so empty-term search still works).
      const nameQuery = supabase
        .from("organizations")
        .select(
          "id, name, facility_id, promo_code, affiliate_id, is_no_reseller",
        )
        .eq("is_archived", false)
        .eq("is_no_reseller", false)
        .order("name", { ascending: true })
        .limit(20);
      if (term) {
        nameQuery.ilike("name", `%${term}%`);
      }
      const { data: nameMatches } = await nameQuery;
      const nameResults = (nameMatches ?? []) as Organization[];

      // Merge: pinned No Reseller → identifier matches → name matches
      // (deduped). Cap visible list at 25 to avoid overwhelming the
      // dropdown.
      const seen = new Set<string>();
      const merged: Organization[] = [];
      const push = (rows: (Organization | null | undefined)[]) => {
        for (const o of rows) {
          if (o && !seen.has(o.id)) {
            seen.add(o.id);
            merged.push(o);
            if (merged.length >= 25) {
              break;
            }
          }
        }
      };
      push([noResellerRow as Organization | null]);
      push(identifierMatches);
      push(nameResults);
      return merged;
    },
    staleTime: 30000,
  });

  // R2A: dropdown label includes the disambiguator (facility_id, then
  // promo_code, then affiliate_id). No Reseller has no identifier and
  // renders as just its name.
  const orgDisplayLabel = (org: Organization): string => {
    if (org.is_no_reseller) {
      return org.name;
    }
    const identifier =
      org.facility_id || org.promo_code || org.affiliate_id || "";
    return identifier ? `${org.name} (${identifier})` : org.name;
  };

  // Close org dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        orgDropdownRef.current &&
        !orgDropdownRef.current.contains(e.target as Node)
      ) {
        setShowOrgDropdown(false);
      }
    };
    if (showOrgDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [showOrgDropdown]);

  // Load locations (with each branch's CTA promo code) and the organization's main address.
  const loadOrgDetails = async (orgId: UUID): Promise<Location[]> => {
    const [locRes, ctaRes, orgRes] = await Promise.all([
      supabase
        .from("locations")
        .select("id, name, organization_id, city, address_line1, is_archived")
        .eq("organization_id", orgId)
        .order("name"),
      supabase
        .from("organization_ctas")
        .select("location_id, parameters, is_active, updated_at")
        .eq("organization_id", orgId)
        .eq("is_active", true),
      supabase
        .from("organizations")
        .select(
          "address_line1, address_city, address_state, address_postal_code, address_country, affiliate_id, promo_code",
        )
        .eq("id", orgId)
        .maybeSingle(),
    ]);
    if (locRes.error) {
      throw locRes.error;
    }
    const promoByLocation = new Map<string, string>();
    let orgLevelPromo: string | null = null;
    for (const c of (ctaRes.data ?? []) as Array<{
      location_id: string | null;
      parameters: Record<string, unknown> | null;
    }>) {
      const promo =
        typeof c.parameters?.promo === "string" && c.parameters.promo.trim()
          ? c.parameters.promo.trim()
          : null;
      if (!promo) {
        continue;
      }
      if (c.location_id) {
        promoByLocation.set(c.location_id, promo);
      } else {
        orgLevelPromo = promo;
      }
    }
    const rows = (
      (locRes.data ?? []) as Array<Location & { is_archived?: boolean | null }>
    )
      .filter((l) => !l.is_archived)
      .map((l) => ({
        id: l.id,
        name: l.name,
        organization_id: l.organization_id,
        city: l.city,
        address_line1: l.address_line1,
        promo: promoByLocation.get(l.id) ?? null,
      }));
    const o = orgRes.data as
      | (Omit<OrgMain, "promo"> & { promo_code: string | null })
      | null;
    setOrgMain((prev) => ({
      ...prev,
      [orgId]: {
        address_line1: o?.address_line1 ?? null,
        address_city: o?.address_city ?? null,
        address_state: o?.address_state ?? null,
        address_postal_code: o?.address_postal_code ?? null,
        address_country: o?.address_country ?? null,
        affiliate_id: o?.affiliate_id ?? null,
        promo: orgLevelPromo ?? o?.promo_code ?? null,
      },
    }));
    setLocations((prev) => ({ ...prev, [orgId]: rows }));
    return rows;
  };

  useEffect(() => {
    if (!selectedOrg || locations[selectedOrg.id]) {
      return;
    }
    loadOrgDetails(selectedOrg.id).catch((error) =>
      console.error("Error loading locations:", error),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrg, locations]);

  // The organization's main address is not one of its locations (e.g. Cindy's Hot Shots: main address
  // Glen Burnie, only location Severn). Offer to add it so the event can be tied to that branch. A
  // location without its own CTA uses the organization-level CTA, i.e. the main address's code.
  const mainNotALocation = (orgId: UUID): OrgMain | null => {
    const m = orgMain[orgId];
    const locs = locations[orgId] ?? [];
    if (!m?.address_city || locs.length === 0) {
      return null;
    }
    // A location represents the main address if its city, its street address, or its NAME matches
    // (some locations carry the branch only in the name, with no city filled in -- QA's Shoot Smart
    // "… - Grand Prairie" has city NULL; matching on city alone offered to create a duplicate).
    const norm = (v?: string | null) => (v ?? "").trim().toLowerCase();
    const city = norm(m.address_city);
    const street = norm(m.address_line1);
    const represented = locs.some(
      (l) =>
        sameCity(l.city, m.address_city) ||
        (street !== "" && norm(l.address_line1) === street) ||
        (city !== "" && norm(l.name).includes(city)),
    );
    return represented ? null : m;
  };

  const handleAddMainAsLocation = async () => {
    if (!selectedOrg) {
      return;
    }
    const m = mainNotALocation(selectedOrg.id);
    if (!m) {
      return;
    }
    setAddingMain(true);
    setAddMainError(null);
    try {
      const { data, error } = await supabase
        .from("locations")
        .insert({
          organization_id: selectedOrg.id,
          name: `${selectedOrg.name} - ${m.address_city}`,
          address_line1: m.address_line1,
          city: m.address_city,
          state: m.address_state,
          postal_code: m.address_postal_code,
          country: m.address_country,
          affiliate_id: m.affiliate_id,
        })
        .select("id")
        .single();
      if (error) {
        throw error;
      }
      await loadOrgDetails(selectedOrg.id);
      setSelectedLocationId((data as { id: string }).id);
    } catch (e) {
      setAddMainError(
        (e as { message?: string })?.message ||
          "Could not add the main address as a location.",
      );
    } finally {
      setAddingMain(false);
    }
  };

  const locationLabel = (loc: Location) => {
    const parts = [loc.name];
    if (loc.city && !loc.name.toLowerCase().includes(loc.city.toLowerCase())) {
      parts.push(`— ${loc.city}`);
    }
    if (loc.promo) {
      parts.push(`· promo ${loc.promo}`);
    }
    return parts.join(" ");
  };

  const handleSelectOrg = (org: Organization) => {
    setSelectedOrg(org);
    setSelectedLocationId("");
    setOrgSearchTerm(org.name);
    setShowOrgDropdown(false);
  };

  const handleClearOrg = () => {
    setSelectedOrg(null);
    setSelectedLocationId("");
    setOrgSearchTerm("");
  };

  const handleAdd = () => {
    if (!selectedOrg) {
      return;
    }

    const newSelection: EventOrgLocationSelection = {
      org_id: selectedOrg.id,
      org_name: selectedOrg.name,
      location_id: selectedLocationId || null,
      location_name: selectedLocationId
        ? locations[selectedOrg.id]?.find((l) => l.id === selectedLocationId)
            ?.name
        : undefined,
    };

    // Check for duplicates
    const isDuplicate = selected.some(
      (s) =>
        s.org_id === newSelection.org_id &&
        s.location_id === newSelection.location_id,
    );

    if (!isDuplicate) {
      onChange([...selected, newSelection]);
    }

    // Reset selection
    handleClearOrg();
  };

  const handleRemove = (index: number) => {
    onChange(selected.filter((_, i) => i !== index));
  };

  const availableLocations = selectedOrg ? locations[selectedOrg.id] || [] : [];

  return (
    <div className="space-y-4">
      {/* Selection Controls */}
      <div className="flex flex-col md:flex-row gap-3">
        <div className="flex-1 relative" ref={orgDropdownRef}>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Organization
          </label>
          <div className="relative">
            <input
              onKeyDown={preventEnterSubmit}
              type="text"
              value={orgSearchTerm}
              onChange={(e) => {
                setOrgSearchTerm(e.target.value);
                setShowOrgDropdown(true);
                if (selectedOrg && e.target.value !== selectedOrg.name) {
                  setSelectedOrg(null);
                  setSelectedLocationId("");
                }
              }}
              onFocus={() => setShowOrgDropdown(true)}
              placeholder="Search organizations..."
              className="w-full rounded-md border border-gray-300 px-3 py-2 pr-10 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
            <MagnifyingGlassIcon className="absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
          </div>

          {showOrgDropdown && orgResults.length > 0 && (
            <div className="absolute z-10 mt-1 w-full rounded-md border border-gray-200 bg-white shadow-lg max-h-48 overflow-y-auto">
              {orgResults.map((org) => (
                <button
                  key={org.id}
                  type="button"
                  onClick={() => handleSelectOrg(org)}
                  className={`w-full px-3 py-2 text-left hover:bg-gray-100 focus:bg-gray-100 focus:outline-none ${
                    org.is_no_reseller
                      ? "border-b border-gray-200 bg-yellow-50"
                      : ""
                  }`}
                >
                  <div className="font-medium text-gray-900">
                    {orgDisplayLabel(org)}
                    {org.is_no_reseller && (
                      <span className="ml-2 text-xs text-yellow-700">
                        (pinned)
                      </span>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}

          {selectedOrg && (
            <div className="mt-1 text-sm text-green-600">
              Selected: {orgDisplayLabel(selectedOrg)}
            </div>
          )}
        </div>

        {selectedOrg && availableLocations.length > 0 && (
          <div className="flex-1">
            <Select
              label="Location (Optional)"
              value={selectedLocationId}
              onChange={(e) => setSelectedLocationId(e.target.value)}
            >
              <option value="">
                {orgMain[selectedOrg.id]?.promo
                  ? `All locations (main address${
                      orgMain[selectedOrg.id]?.address_city
                        ? ` — ${orgMain[selectedOrg.id]?.address_city}`
                        : ""
                    } · promo ${orgMain[selectedOrg.id]?.promo})`
                  : "All locations"}
              </option>
              {availableLocations.map((loc) => (
                <option key={loc.id} value={loc.id}>
                  {locationLabel(loc)}
                </option>
              ))}
            </Select>
            {mainNotALocation(selectedOrg.id) && (
              <div className="mt-1 text-xs text-gray-600">
                The main address (
                {mainNotALocation(selectedOrg.id)?.address_city}) isn&apos;t a
                location yet.{" "}
                <button
                  type="button"
                  onClick={handleAddMainAsLocation}
                  disabled={addingMain}
                  className="font-medium text-primary-700 underline disabled:text-gray-400"
                >
                  {addingMain
                    ? "Adding…"
                    : `Add ${mainNotALocation(selectedOrg.id)?.address_city} as a location`}
                </button>
              </div>
            )}
            {addMainError && (
              <p role="alert" className="mt-1 text-xs text-red-700">
                {addMainError}
              </p>
            )}
          </div>
        )}

        <div className="flex flex-col">
          {/* Invisible spacer mirrors the input labels above so the Add
              button aligns with the input row, not the bottom of the
              org column (which can grow with the "Selected:" caption). */}
          <label
            className="block text-sm font-medium text-gray-700 mb-1 invisible"
            aria-hidden="true"
          >
            Add
          </label>
          <Button
            type="button"
            onClick={handleAdd}
            disabled={!selectedOrg}
            variant="secondary"
          >
            Add
          </Button>
        </div>
      </div>

      {/* Selected Items */}
      {selected.length > 0 && (
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            Selected Organizations/Locations
          </label>
          <div className="space-y-2">
            {selected.map((selection, index) => (
              <div
                key={`${selection.org_id}-${selection.location_id}-${index}`}
                className="flex items-center justify-between bg-gray-50 p-3 rounded-lg"
              >
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">
                    {selection.org_name}
                  </p>
                  {selection.location_name && (
                    <p className="text-xs text-gray-600">
                      Location: {selection.location_name}
                    </p>
                  )}
                  {!selection.location_name && (
                    <p className="text-xs text-gray-500 italic">
                      All locations
                    </p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => handleRemove(index)}
                  className="ml-4 text-red-600 hover:text-red-800 text-sm font-medium"
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {selected.length === 0 && (
        <p className="text-sm text-gray-500 italic">
          No organizations or locations selected. Event will be visible to all.
        </p>
      )}
    </div>
  );
};

export default EventOrganizationLocationSelector;
